import type { Container, Sprite } from "pixi.js";
import { ExposureFilter } from "../../filters/ExposureFilter";
import type { TweenEasing } from "../../runner/Easing";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { SymbolDimExposurePort } from "../win-highlight/SymbolDimSession";
import type { SymbolWinAnimationRequest, SymbolWinEffect } from "./SymbolWinEffect";
import type { SymbolWinBrightnessTuning } from "./SymbolWinBrightnessTuning";

interface Entry {
  /** What carries the exposure (`dimNodeResolver`; by default the sprite). */
  node: Container;
  /** The exposure applied last: a new ramp starts from it. */
  current: number;
  /** Bumped per op on this sprite: a stale ramp stops writing. */
  gen: number;
  /** The brightening half, on the node only while the exposure is above 0. */
  filter: ExposureFilter | null;
}

/**
 * The win shine that owns the dim (the Cocos template's `SymbolWinBrightnessAnimator`): one signed exposure per cell,
 * baseline 0, `rgb × (1 + exposure)`. As the `SymbolDimExposurePort` it darkens a cell toward the highlight's negative
 * `dimExposure` and back; as the `SymbolWinEffect` it shines a winner 0 → `peak` → 0, so a winner the highlight dimmed
 * (`dimWinners`) flows from the dim straight up into its shine, and `onShineStart` reveals it then. Both halves write
 * the same value, which is why it is one owner.
 *
 * The darkening half is a grey tint on the cell's node (no filter, batched); the brightening half an `ExposureFilter`
 * held only while the exposure is above 0. With `once`, a cell shines once per highlight session (a win cycle's
 * re-highlight does not re-shine it) until `stopAll` or its `release`. A title releases a cell when its view is
 * rebound (`GridLayer.onSymbolBound`), so a recycled cell comes back clean; the reels' spin-start teardown
 * (`stopAll({ revert: false })`) stops the ramps and leaves the scrolling cells as they are. Every ramp runs on the
 * runner (engine time), generation-guarded per cell.
 */
export class SymbolWinBrightnessEffect implements SymbolWinEffect, SymbolDimExposurePort {
  private readonly runner: PresentationTaskRunner;
  private readonly tuning: SymbolWinBrightnessTuning;
  private readonly once: boolean;
  private readonly nodeOf: (sprite: Sprite) => Container | null;
  private readonly entries = new Map<Sprite, Entry>();
  /** The cells shone this session (`once`): apart from `entries`, which the dim fills too. */
  private readonly shined = new Set<Sprite>();
  private generation = 0;

  public constructor(input: {
    taskRunner: PresentationTaskRunner;
    tuning: SymbolWinBrightnessTuning;
    /** Shine a cell once per highlight session (default false: every `play`). */
    once?: boolean;
    /**
     * What carries a cell's exposure: default the sprite; e.g. `(s) => s.parent`, the cell's content node, so the
     * dim and the shine cover everything at the cell (an alt-frame overlay over the sprite included).
     */
    dimNodeResolver?: (sprite: Sprite) => Container | null;
  }) {
    this.runner = input.taskRunner;
    this.tuning = input.tuning;
    this.once = input.once ?? false;
    this.nodeOf = input.dimNodeResolver ?? ((sprite) => sprite);
  }

  public play(request: SymbolWinAnimationRequest): void {
    const sprite = request.symbolSprite;
    if (!sprite || sprite.destroyed) return;
    if (this.once && this.shined.has(sprite)) return;
    const entry = this.entryFor(sprite);
    if (!entry) return;
    this.shined.add(sprite);
    const gen = this.nextGeneration(entry);
    const t = this.tuning;
    void (async () => {
      if (t.startDelayMilliseconds > 0) {
        await this.runner.sleep(t.startDelayMilliseconds / 1000);
        if (!this.isCurrent(sprite, gen)) return;
      }
      request.onShineStart?.();
      await this.ramp(sprite, entry, gen, t.peak, t.rampUpSeconds, t.rampUpEasing);
      if (!this.isCurrent(sprite, gen)) return;
      if (t.holdSeconds > 0) {
        await this.runner.sleep(t.holdSeconds);
        if (!this.isCurrent(sprite, gen)) return;
      }
      await this.ramp(sprite, entry, gen, 0, t.rampDownSeconds, t.rampDownEasing);
    })();
  }

  /** Every ramp stops; with `revert: false` the cells keep what they show (their rebind releases them), else they are released. */
  public stopAll(opts?: { revert?: boolean }): void {
    this.shined.clear();
    if (opts?.revert === false) {
      for (const entry of this.entries.values()) this.nextGeneration(entry);
      return;
    }
    for (const sprite of [...this.entries.keys()]) this.release(sprite);
  }

  public getPlaySeconds(): number {
    const t = this.tuning;
    return t.startDelayMilliseconds / 1000 + t.rampUpSeconds + t.holdSeconds + t.rampDownSeconds;
  }

  public rampExposure(sprite: Sprite | null, target: number, seconds: number, easing: TweenEasing, onComplete?: () => void, startDelaySeconds = 0): void {
    if (!sprite || sprite.destroyed) return;
    const entry = this.entryFor(sprite);
    if (!entry) return;
    const gen = this.nextGeneration(entry);
    void (async () => {
      if (startDelaySeconds > 0) {
        await this.runner.sleep(startDelaySeconds);
        if (!this.isCurrent(sprite, gen)) return;
      }
      await this.ramp(sprite, entry, gen, target, seconds, easing);
      if (this.isCurrent(sprite, gen)) onComplete?.();
    })();
  }

  /** The cell back to untouched (no tint, no filter) and forgotten; idempotent. */
  public release(sprite: Sprite): void {
    const entry = this.entries.get(sprite);
    if (!entry) return;
    this.nextGeneration(entry);
    this.entries.delete(sprite);
    this.shined.delete(sprite);
    this.apply(entry, 0);
  }

  private async ramp(sprite: Sprite, entry: Entry, gen: number, target: number, seconds: number, easing: TweenEasing): Promise<void> {
    const from = entry.current;
    if (seconds > 0) {
      await this.runner.tweenProgress(
        seconds,
        (k) => {
          if (this.isCurrent(sprite, gen)) this.apply(entry, from + (target - from) * k);
        },
        undefined,
        { easing },
      );
    }
    if (this.isCurrent(sprite, gen)) this.apply(entry, target);
  }

  /** `rgb × (1 + exposure)`: a grey tint for the dark half, the filter for the bright half. */
  private apply(entry: Entry, exposure: number): void {
    entry.current = exposure;
    const node = entry.node;
    if (node.destroyed) return;
    const level = Math.round(Math.max(0, Math.min(1, 1 + exposure)) * 255);
    node.tint = (level << 16) | (level << 8) | level;
    if (exposure > 0) {
      const filter = (entry.filter ??= new ExposureFilter(0));
      filter.highlight = exposure;
      if (!node.filters?.includes(filter)) node.filters = [...(node.filters ?? []), filter];
    } else if (entry.filter && node.filters?.includes(entry.filter)) {
      const filter = entry.filter;
      node.filters = node.filters.filter((f) => f !== filter);
    }
  }

  private entryFor(sprite: Sprite): Entry | null {
    const known = this.entries.get(sprite);
    if (known) return known;
    const node = this.nodeOf(sprite);
    if (!node || node.destroyed) return null;
    const entry: Entry = { node, current: 0, gen: 0, filter: null };
    this.entries.set(sprite, entry);
    return entry;
  }

  private nextGeneration(entry: Entry): number {
    entry.gen = ++this.generation;
    return entry.gen;
  }

  private isCurrent(sprite: Sprite, gen: number): boolean {
    return !sprite.destroyed && this.entries.get(sprite)?.gen === gen;
  }
}
